import { useState, useEffect } from 'react';
import { authApi } from '../services/api';
import { getTenant, getSubdomain } from '../services/tenant';
import { AlertTriangle, Shield } from 'lucide-react';
import { brandAsset, useBrand } from '@/brand';
import { Button } from '@/components/ds-zta';

export default function Login() {
  const brand = useBrand();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);

  useEffect(() => {
    // Check if user was redirected due to token expiration
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('expired') === 'true') {
      setShowExpiredMessage(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOIDCLogin = async () => {
    setIsLoading(true);
    setError('');

    // Preferred: the single fixed d2auth callback. The server issues a state that
    // remembers this tenant for the duration of the redirect, so d2auth only ever
    // needs one registered callback URL for the whole product.
    try {
      const tenant = getTenant() || getSubdomain();
      const { data } = await authApi.pkceStart(tenant);
      window.location.href = data.auth_url;
      return;
    } catch (err: any) {
      // 501 = configured off; 404 = an API that predates this flow entirely.
      const unavailable =
        err.response?.status === 501 ||
        err.response?.status === 404 ||
        err.response?.data?.code === 'PKCE_DISABLED';
      if (!unavailable) {
        setError(err.response?.data?.error || 'Authentication failed');
        setIsLoading(false);
        return;
      }
      // AUTH_D2AUTH_REDIRECT_URI is not configured on this deployment — fall
      // through to the legacy flow, which derives a per-tenant callback URL from
      // the request origin and needs that URL registered with d2auth.
    }

    try {
      const response = await authApi.getAuthUrl();
      // Redirect to D2Auth for authentication
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Authentication failed');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--d2d-bg-primary)] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* d2d Logo Lockup: the brand mark (src/brand.ts), the wordmark in Britti Sans 500 at
              line-height 1.0 (cascade plan 3.3). */}
          <img src={brandAsset(brand.mark)} alt="" aria-hidden="true" className="mx-auto h-12 w-12" />
          <h1 className="mt-6 text-center font-display text-heading-sm font-medium leading-none text-[var(--d2d-text-primary)]">
            {brand.name}
          </h1>
          <p className="mt-2 text-center text-sm text-[var(--d2d-text-secondary)]">
            Sign in to your admin account
          </p>
        </div>
        <div className="mt-8 space-y-6">
          {showExpiredMessage && (
            <div className="rounded-md bg-[var(--zta-tint-warning)] p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-[var(--d2d-warning)]" />
                </div>
                <div className="ml-3">
                  <div className="text-sm text-[var(--d2d-warning)]">
                    Your session has expired. Please sign in again to continue.
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="rounded-md bg-[var(--zta-tint-danger)] p-4">
              <div className="text-sm text-[var(--danger-text)]">{error}</div>
            </div>
          )}
          
          <div>
            <Button
              className="w-full"
              onClick={handleOIDCLogin}
              disabled={isLoading}
              loading={isLoading}
              leadingIcon={<Shield className="w-5 h-5" />}
            >
              {isLoading ? 'Redirecting to D2Auth...' : 'Sign in with SSO'}
            </Button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              You will be redirected to D2Auth for secure authentication
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
